import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  connectAuthPost, getConnectToken, getConnectUser,
  clearConnectSession, normalizeUser, normalizePost
} from './connectApi';
import PostComments from './PostComments';
import NotificationsBell from './NotificationsBell';
import ConnectNav from './ConnectNav';
import FollowsList from './FollowsList';
import './styles/ConnectProfile.css';
import './styles/ProfileLayout.css';
import './styles/ConnectMeet.css';
import './styles/ConnectSetup.css';

/**
 * Full-page view of another member's profile.
 * Route: /connect/user/:id
 */
export default function ConnectUserProfile() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [viewer, setViewer] = useState(null);
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  // 'followers' | 'following' while that list is open
  const [followsTab, setFollowsTab] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');
  const [openCommentsPostId, setOpenCommentsPostId] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetail, setReportDetail] = useState('');
  const [safetyNotice, setSafetyNotice] = useState('');

  const formatTimeAgo = (timestamp) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  useEffect(() => {
    if (!getConnectToken()) {
      navigate('/connect');
      return;
    }
    setViewer(getConnectUser());

    const userId = parseInt(id, 10);
    if (!userId) {
      setNotFound(true);
      setIsLoading(false);
      return;
    }

    // Own profile lives on its own page
    const own = getConnectUser();
    if (own && own.id === userId) {
      navigate('/connect/profile');
      return;
    }

    const load = async () => {
      try {
        const result = await connectAuthPost('connect/profile/get', { user_id: userId });
        if (!result.success) {
          setNotFound(true);
          return;
        }
        setProfile(normalizeUser(result.data.user));

        const postsResult = await connectAuthPost('connect/posts/list', { user_id: userId });
        if (postsResult.success) {
          setPosts(postsResult.data.posts.map(normalizePost));
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [id, navigate]);

  const handleFollow = async () => {
    try {
      const result = await connectAuthPost('connect/users/follow', { user_id: profile.id });
      if (result.success) {
        setProfile(prev => ({
          ...prev,
          isFollowing: result.data.is_following,
          followers: result.data.followers
        }));
      }
    } catch (err) {
      console.error('Failed to toggle follow:', err);
    }
  };

  // Blocking is mutual and enforced server-side, so it removes this person
  // from every list, count and notification at once.
  const handleBlock = async () => {
    const ok = window.confirm(
      `Block ${profile.fullName}? You will disappear from each other's lists, and any following between you ends.`
    );
    if (!ok) return;
    try {
      const res = await connectAuthPost('connect/users/block', { user_id: profile.id });
      if (res.success) navigate('/connect/discover');
      else setSafetyNotice(res.response || 'Could not block.');
    } catch (err) {
      setSafetyNotice('Could not reach the server.');
    }
  };

  const handleReport = async () => {
    if (!reportReason) { setSafetyNotice('Pick a reason.'); return; }
    try {
      const res = await connectAuthPost('connect/reports/create', {
        target_type: 'user',
        target_id: profile.id,
        reason: reportReason,
        detail: reportDetail,
      });
      setSafetyNotice(res.response || '');
      setShowReport(false);
      setReportReason('');
      setReportDetail('');
    } catch (err) {
      setSafetyNotice('Could not reach the server.');
    }
  };

  const handleToggleLike = async (postId) => {
    try {
      const result = await connectAuthPost('connect/posts/like', { post_id: postId });
      if (result.success) {
        setPosts(prev => prev.map(p =>
          p.id === postId
            ? { ...p, likes: result.data.likes, likedByViewer: result.data.liked }
            : p
        ));
      }
    } catch (err) {
      console.error('Failed to toggle like:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await connectAuthPost('connect/auth/logout');
    } catch (err) {
      // Best-effort — clear locally regardless
    }
    clearConnectSession();
    navigate('/connect');
  };

  if (!viewer) return null;

  return (
    <div className="connect-profile-page">
      {/* Header */}
      <header className="connect-profile-header">
        <Link to="/" className="connect-profile-logo">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="OutCaribbean" />
        </Link>
        <ConnectNav active="connect" />
        <div className="connect-header-actions">
          <NotificationsBell />
          <button className="connect-logout-btn" onClick={handleLogout}>
            Log Out
          </button>
          <button className="connect-mobile-nav-toggle" onClick={() => setShowMobileNav(true)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </header>

      {/* Mobile Navigation */}
      {showMobileNav && (
        <>
          <div className="connect-mobile-nav-overlay active" onClick={() => setShowMobileNav(false)}></div>
          <div className="connect-mobile-nav-menu active">
            <div className="connect-mobile-nav-header">
              <h3>Menu</h3>
              <button className="connect-mobile-nav-close" onClick={() => setShowMobileNav(false)}>×</button>
            </div>
            <div className="connect-mobile-nav-links">
              <ConnectNav active="connect" variant="mobile" onNavigate={() => setShowMobileNav(false)} />
            </div>
            <div className="connect-mobile-nav-footer">
              <button className="connect-logout-btn" onClick={handleLogout}>Log Out</button>
            </div>
          </div>
        </>
      )}

      <main className="cpx-main">
        <button className="cpx-back" onClick={() => navigate('/connect/discover')}>
          ← Back to Discover
        </button>

        {isLoading ? (
          <div className="cpx-state">
            <h3>Loading profile...</h3>
          </div>
        ) : notFound ? (
          <div className="cpx-state">
            <h3>Profile not found</h3>
            <p>This profile doesn't exist or is private.</p>
          </div>
        ) : (
          <>
            {/* Cover */}
            <div className="cpx-cover">
              {profile.coverPhoto ? (
                <img src={profile.coverPhoto} alt="Cover" />
              ) : (
                <div className="cpx-cover-placeholder"></div>
              )}
            </div>

            <div className="cpx-layout">
              {/* Identity card */}
              <aside className="cpx-card">
                <div className="cpx-avatar">
                  {profile.profilePicture ? (
                    <img src={profile.profilePicture} alt={profile.fullName} />
                  ) : (
                    <div className="cpx-avatar-placeholder">
                      {profile.fullName ? profile.fullName.charAt(0).toUpperCase() : '?'}
                    </div>
                  )}
                </div>

                <h1 className="cpx-name">{profile.fullName}</h1>
                {profile.pronouns && (
                  <span className="cpx-pronouns">{profile.pronouns}</span>
                )}
                {profile.isLocalGuide && (
                  <span className="cpx-guide-badge">🌴 Local Guide</span>
                )}

                {profile.currentLocation && (
                  <p className="cpx-location">📍 {profile.currentLocation}</p>
                )}

                <div className="cpx-stats">
                  <button type="button" className="cpx-stat-btn" onClick={() => setFollowsTab('followers')}>
                    <strong>{profile.followers}</strong>
                    <span>Followers</span>
                  </button>
                  <button type="button" className="cpx-stat-btn" onClick={() => setFollowsTab('following')}>
                    <strong>{profile.following}</strong>
                    <span>Following</span>
                  </button>
                  <div>
                    <strong>{posts.length}</strong>
                    <span>Posts</span>
                  </div>
                </div>

                {(profile.genderIdentity || profile.sexualOrientation || profile.travelStyle) && (
                  <>
                    <div className="cpx-label">Identity</div>
                    <div className="cpx-tags">
                      {profile.genderIdentity && (
                        <span className="cpx-tag">{profile.genderIdentity}</span>
                      )}
                      {profile.sexualOrientation && (
                        <span className="cpx-tag">{profile.sexualOrientation}</span>
                      )}
                      {profile.travelStyle && (
                        <span className="cpx-tag travel">{profile.travelStyle}</span>
                      )}
                    </div>
                  </>
                )}

                {profile.restricted && (
                  <div className="cpx-locked">
                    <div className="cpx-label">Followers only</div>
                    <p>
                      {profile.fullName} shares their profile and posts with the
                      people who follow them. Follow to see more.
                    </p>
                  </div>
                )}

                {profile.bio && (
                  <>
                    <div className="cpx-label">About</div>
                    <p className="cpx-bio">{profile.bio}</p>
                  </>
                )}

                {profile.trips.length > 0 && (
                  <>
                    <div className="cpx-label">Trips</div>
                    <ul className="cpx-trips">
                      {profile.trips.map(trip => (
                        <li key={trip.destination_id} className="cpx-trip">
                          <strong>{trip.destination_name}</strong>
                          <span>{trip.is_now ? `There now · ${trip.when_label}` : trip.when_label}</span>
                          {trip.open_to_connect && <span>Open to meeting</span>}
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {profile.favoriteDestinations.length > 0 && (
                  <>
                    <div className="cpx-label">Favorite destinations</div>
                    <div className="cpx-stamps">
                      {profile.favoriteDestinations.map(dest => (
                        <span key={dest} className="cpx-stamp">{dest}</span>
                      ))}
                    </div>
                  </>
                )}

                <div className="cpx-actions">
                  <button
                    className={`cpx-btn primary ${profile.isFollowing ? 'following' : ''}`}
                    onClick={handleFollow}
                  >
                    {profile.isFollowing ? '✓ Following' : '+ Follow'}
                  </button>
                  <Link to={`/connect/messages?user=${profile.id}`} className="cpx-btn ghost">
                    Message
                  </Link>
                </div>

                <div className="cpx-safety">
                  <button className="cpx-safety-link" onClick={() => setShowReport(true)}>
                    Report
                  </button>
                  <button className="cpx-safety-link" onClick={handleBlock}>
                    Block
                  </button>
                </div>
              </aside>

              {/* Feed */}
              <section className="cpx-feed">
                <div className="cpx-tabs">
                  <button
                    className={`cpx-tab ${activeTab === 'posts' ? 'active' : ''}`}
                    onClick={() => setActiveTab('posts')}
                  >
                    Posts ({posts.length})
                  </button>
                  <button
                    className={`cpx-tab ${activeTab === 'photos' ? 'active' : ''}`}
                    onClick={() => setActiveTab('photos')}
                  >
                    Photos
                  </button>
                </div>

                {activeTab === 'posts' && (
                  <div className="connect-posts-list">
                    {posts.length === 0 && (
                      <div className="cpx-state">
                        <h3>{profile.restricted ? 'Posts are for followers' : 'No posts yet'}</h3>
                        <p>
                          {profile.restricted
                            ? `Follow ${profile.fullName} to see what they share.`
                            : `${profile.fullName} hasn't shared anything yet`}
                        </p>
                      </div>
                    )}
                    {posts.map(post => (
                      <div key={post.id} className="connect-post-card">
                        <div className="connect-post-header">
                          <div className="connect-post-avatar">
                            {profile.profilePicture ? (
                              <img src={profile.profilePicture} alt={profile.fullName} />
                            ) : (
                              <div className="connect-post-avatar-placeholder">
                                {profile.fullName ? profile.fullName.charAt(0).toUpperCase() : '?'}
                              </div>
                            )}
                          </div>
                          <div className="connect-post-meta">
                            <span className="connect-post-author">{profile.fullName}</span>
                            <span className="connect-post-time">
                              {formatTimeAgo(post.timestamp)}
                              {post.location && ` · 📍 ${post.location}`}
                            </span>
                          </div>
                        </div>
                        <p className="connect-post-content">{post.content}</p>
                        {post.image && (
                          <div className="connect-post-image">
                            <img src={post.image} alt="Post" />
                          </div>
                        )}
                        <div className="connect-post-actions">
                          <button
                            className={`connect-post-action ${post.likedByViewer ? 'liked' : ''}`}
                            onClick={() => handleToggleLike(post.id)}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill={post.likedByViewer ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                            </svg>
                            <span>{post.likes}</span>
                          </button>
                          <button
                            className={`connect-post-action ${openCommentsPostId === post.id ? 'liked' : ''}`}
                            onClick={() => setOpenCommentsPostId(prev => prev === post.id ? null : post.id)}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                            </svg>
                            <span>{post.comments}</span>
                          </button>
                        </div>
                        {openCommentsPostId === post.id && (
                          <PostComments
                            postId={post.id}
                            onCountChange={(pid, count) =>
                              setPosts(prev => prev.map(p => p.id === pid ? { ...p, comments: count } : p))
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'photos' && (
                  <div className="connect-photos-grid">
                    {posts.filter(p => p.image).length === 0 && (
                      <div className="cpx-state">
                        <h3>No photos yet</h3>
                      </div>
                    )}
                    {posts.filter(p => p.image).map(post => (
                      <div key={post.id} className="connect-photo-item">
                        <img src={post.image} alt="From a post" />
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </main>

      {safetyNotice && (
        <div className="cpx-safety-toast" role="status">{safetyNotice}</div>
      )}

      {showReport && (
        <div className="meet-overlay" onClick={() => setShowReport(false)}>
          <div className="meet-modal" onClick={(e) => e.stopPropagation()}>
            <div className="meet-modal-head">
              <h2>Report {profile.fullName}</h2>
              <button className="meet-modal-close" onClick={() => setShowReport(false)}>×</button>
            </div>
            <div className="meet-modal-body">
              <p className="setup-hint" style={{ marginTop: 0 }}>
                This goes to a moderator. They see what you pick and anything you add.
                {' '}They judge it against the{' '}
                <Link to="/community-guidelines" target="_blank" rel="noreferrer">
                  Community Guidelines
                </Link>.
              </p>

              <label className="meet-label">Reason</label>
              <select className="meet-input" value={reportReason}
                      onChange={(e) => setReportReason(e.target.value)}>
                <option value="">Choose a reason</option>
                <option value="harassment">Harassment or abuse</option>
                <option value="fake">Fake or impersonating someone</option>
                <option value="unsafe">Made me feel unsafe</option>
                <option value="spam">Spam or scam</option>
                <option value="underage">Appears to be under 18</option>
                <option value="other">Something else</option>
              </select>

              <label className="meet-label">Anything else <span className="meet-hint">optional</span></label>
              <textarea className="meet-input" rows={3} value={reportDetail}
                        onChange={(e) => setReportDetail(e.target.value)}
                        placeholder="What happened?" />
            </div>
            <div className="meet-modal-foot">
              <button className="meet-btn ghost" onClick={() => setShowReport(false)}>Cancel</button>
              <button className="meet-btn danger" onClick={handleReport}>Send report</button>
            </div>
          </div>
        </div>
      )}

      {followsTab && profile && (
        <FollowsList
          userId={profile.id}
          initialTab={followsTab}
          onClose={() => setFollowsTab(null)}
        />
      )}
    </div>
  );
}
